import { INDICATOR_GALLERIES } from '../data/indicatorImages';

const COVER_BASE = '/images/Divers/Product_Cover';
const COVER_16 = `${COVER_BASE}/1600x1000`;
const COVER_LEGACY = `${COVER_BASE}/TEST_IA`;

const SLUG_GALLERIES = {
  'volume-profile-premium': 'PremiumVolumeProfile',
  'vwap-premium': 'PremiumVwap',
  'vwap-lite': 'LiteVwap',
  'volume-profile-lite': 'LiteVolumeProfile',
  'trading-sessions': 'TradingSessions',
  'asian-session-levels': 'AsianSession',
  'enhanced-session-levels': 'EnhancedSessionLevels',
  'risk-reward': 'RiskReward',
  orb: 'ORB',
  'open-levels': 'OpenLevels',
  'bar-timer': 'BarTimer',
};

const SLUG_ALIASES = {
  'volume-profile': 'volume-profile-premium',
  'vwap-avance': 'vwap-premium',
  'ny-price-action': 'nypa',
  'fun-bar-timer': 'bar-timer',
  'footprint-pro': 'footprint',
  'cumulative-delta-pro': 'cumulative-delta',
  'economic-news': 'economic-calendar',
  'asian-session': 'asian-session-levels',
};

/**
 * Couvertures marketing (cards / première image de la fiche).
 * Les captures chart du slider viennent de INDICATOR_GALLERIES.
 */
export const PRODUCT_COVERS = {
  'volume-profile-premium': `${COVER_16}/VolumeprofillPremiumCover.png`,
  'vwap-premium': `${COVER_16}/VwapPremiumCover.png`,
  'vwap-lite': `${COVER_16}/AlturionVwapLiteCover.png`,
  'volume-profile-lite': `${COVER_16}/VolumeprofileLiteCover.png`,
  'trading-sessions': `${COVER_16}/AlturionTradingSession.png`,
  'asian-session-levels': `${COVER_16}/AsianSessionCover.png`,
  'enhanced-session-levels': `${COVER_16}/SessionLevelsCover.png`,
  'risk-reward': `${COVER_16}/Risk_rewardCover.png`,
  orb: `${COVER_16}/ORBCover.png`,
  'open-levels': `${COVER_16}/OpenLevelsCover.png`,
  nypa: `${COVER_16}/NYPACover.png`,
  'bar-timer': `${COVER_16}/BarTimerCover.png`,
  'account-locker': '/images/Dashboard/AlturionDashboard1.png',
  footprint: '/images/Footprint/AlturionfootprintPro1.png',
  'cumulative-delta': `${COVER_LEGACY}/alturion-cumulative-delta-pro.png`,
  'delta-profile': '/images/DeltaProfile/AlturionDeltaProfile1.webp',
  'big-trades': '/images/BigTrades/AlturionBigTrade1.webp',
};

/**
 * Captures du slider, après la cover.
 * Produits bientôt disponibles inclus : la page Indicateurs les filtre à part.
 */
const chartShots = Object.fromEntries(
  Object.entries(SLUG_GALLERIES).map(([slug, key]) => [
    slug,
    INDICATOR_GALLERIES[key],
  ])
);

export const PRODUCT_GALLERY_SHOTS = {
  ...chartShots,
  'delta-profile': [
    '/images/DeltaProfile/AlturionDeltaProfile1.webp',
    '/images/DeltaProfile/AlturionDeltaProfile2.webp',
    '/images/DeltaProfile/AlturionDeltaProfile3.webp',
    '/images/DeltaProfile/AlturionDeltaProfile4.png',
  ],
  'account-locker': [
    '/images/Dashboard/AlturionDashboard1.png',
    '/images/Dashboard/AlturionDashboard2.png',
    '/images/Dashboard/AlturionDashboard3.png',
    '/images/Dashboard/AlturionDashboard4.webp',
  ],
  nypa: [
    '/images/NYPA/AlturionNYPA1.webp',
    '/images/NYPA/AlturionNYPA2.webp',
    '/images/NYPA/AlturionNYPA3.webp',
    '/images/NYPA/AlturionNYPA4.webp',
  ],
  'cumulative-delta': [`${COVER_LEGACY}/alturion-cumulative-delta-pro.png`],
  'big-trades': [
    '/images/BigTrades/AlturionBigTrade1.webp',
    '/images/BigTrades/AlturionBigTrade2.webp',
  ],
};

const PUBLIC_COVER_PATHS = new Set(Object.values(PRODUCT_COVERS));

const resolveSlug = (slug) => SLUG_ALIASES[slug] || slug;

/** Covers servies par CRA `public/` — ne pas passer par getAssetUrl (backend). */
export const isPublicCoverPath = (src) =>
  typeof src === 'string' && PUBLIC_COVER_PATHS.has(src);

/**
 * @param {{ slug?: string }} product
 * @returns {string|null}
 */
export const getProductCover = (product) => {
  if (!product?.slug) return null;
  return PRODUCT_COVERS[resolveSlug(product.slug)] || null;
};

/**
 * Image principale pour cards / fallback fiche.
 * Cover marketing en priorité, sinon image DB.
 */
export const getProductCardImage = (product) => {
  return (
    getProductCover(product) ||
    product?.image ||
    product?.images?.[0] ||
    '/images/placeholder.png'
  );
};

/**
 * Galerie fiche produit : cover en premier, puis screenshots DB (sans doublon).
 */
export const getProductGalleryImages = (product) => {
  const cover = getProductCover(product);
  const slug = product?.slug ? resolveSlug(product.slug) : null;
  const override = slug ? PRODUCT_GALLERY_SHOTS[slug] : null;
  const shots = override
    ? override
    : Array.isArray(product?.images)
      ? product.images.filter(Boolean)
      : [];
  const fallback = product?.image ? [product.image] : [];
  const rest = shots.length > 0 ? shots : fallback;

  if (!cover) return rest.length > 0 ? rest : ['/images/placeholder.png'];

  const withoutCover = rest.filter((src) => src !== cover);
  return [cover, ...withoutCover];
};
